// Utils
import { classifyError } from './SyncErrors';
import { OP_STATUS, LANES } from './Outbox';

// Délais entre deux tentatives après une erreur temporaire : 5 s, 15 s, 30 s, 1 min, 2 min, puis 5 min
export const BACKOFF_MS = [5000, 15000, 30000, 60000, 120000, 300000];
// Délai propre à une requête (distinct des 5 s d'attente d'un formulaire)
export const DEFAULT_TIMEOUT_MS = 60000;
// Au-delà, une erreur 5xx répétée fait passer l'opération en échec
export const MAX_SERVER_ERROR_ATTEMPTS = 8;
// Au-delà, une requête sans réponse alors que l'API répond (500 sans CORS) fait passer l'opération en échec ;
// voie des données seulement, un gros envoi de photo sur une 4G faible échouant souvent alors que la sonde répond
export const MAX_UNANSWERED_ATTEMPTS = 5;
const LOCK_NAME = 'grality-offline-sync';

// Délai avant la prochaine tentative selon le nombre d'essais
export const nextRetryDelay = (attempts) => BACKOFF_MS[Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1];

// Moteur d'envoi : une opération à la fois par voie (données, photos), dans l'ordre de la file et selon les dépendances
export const createSyncEngine = ({
    outbox, idMap, snapshot, syncLog, handlers,
    transport,
    getAuthToken = () => null,
    getConnectionId = () => undefined,
    // Sonde de l'API (Connectivity.probe) : distingue une vraie coupure d'une requête sans réponse alors que l'API répond
    isApiReachable = async () => false,
    onEvent = () => { },
    extras = {}
}) => {
    // Façades qui attendent l'issue d'une opération (sendNow), par id d'opération
    const waiters = new Map();
    const runs = { [LANES.DATA]: null, [LANES.FILES]: null };
    const reruns = { [LANES.DATA]: false, [LANES.FILES]: false };
    // Après une erreur réseau, la voie reste fermée jusqu'à cette heure
    const laneRetryAt = { [LANES.DATA]: 0, [LANES.FILES]: 0 };
    let stopped = true, timer = null;

    // Transmet un évènement à l'interface sans laisser une erreur d'écouteur arrêter le moteur
    const emit = (event) => {
        try { onEvent(event); }
        catch (error) { console.error(error); }
    };

    // Libellé de l'opération pour le journal
    const describeOp = (op) => {
        try { return handlers[op.type]?.describe?.(op) || op.type; }
        catch { return op.type; }
    };

    // Donne l'issue d'une opération aux façades qui l'attendent
    const settle = (opId, outcome) => {
        const callbacks = waiters.get(opId);
        if (!callbacks) return;
        waiters.delete(opId);
        callbacks.forEach(callback => callback(outcome));
    };

    // Ajoute le jeton, la clé d'idempotence et la date de saisie
    const withHeaders = (request, op) => ({
        ...request,
        headers: {
            ...(request.headers || {}),
            Authorization: `Bearer ${getAuthToken()}`,
            ...(op ? { 'Idempotency-Key': op.id, 'X-Captured-At': op.capturedAt } : {})
        }
    });

    // Marque les erreurs venues du réseau ou de l'API, pour les distinguer d'un bogue de gestionnaire
    const send = (config, options) => transport(config, options).catch(error => {
        if (error && typeof error === 'object') error.isTransportError = true;
        throw error;
    });

    // Contexte donné aux gestionnaires
    const context = {
        ...extras,
        idMap, snapshot, outbox, syncLog, getConnectionId,
        request: (config) => send(withHeaders({ timeout: DEFAULT_TIMEOUT_MS, ...config }))
    };

    // « ready », « waiting » (une dépendance est en file) ou « blocked » (une dépendance a échoué)
    const dependencyState = async (op) => {
        for (const dependencyId of op.dependsOn || []) {
            const dependency = await outbox.get(dependencyId);
            if (!dependency) continue;
            if (dependency.status === OP_STATUS.FAILED || dependency.status === OP_STATUS.BLOCKED) {
                if (dependency.softDependents) continue;
                return 'blocked';
            }
            return 'waiting';
        }
        // Attente souple : seulement tant que l'opération attendue peut encore partir
        for (const dependencyId of op.softDependsOn || []) {
            const dependency = await outbox.get(dependencyId);
            if (dependency && dependency.status !== OP_STATUS.FAILED && dependency.status !== OP_STATUS.BLOCKED) return 'waiting';
        }
        return 'ready';
    };

    // Envoie une opération ; renvoie « done » (acceptée, écartée ou refusée) ou « network » (erreur temporaire)
    const processOp = async (queuedOp) => {
        const handler = handlers[queuedOp.type];
        if (!handler) {
            const error = { kind: 'fatal', reason: 'unknown_type', message: queuedOp.type };
            await outbox.markFailed(queuedOp.id, error);
            emit({ type: 'op-failed', op: queuedOp, error });
            settle(queuedOp.id, { status: 'failed', error });
            return 'done';
        }
        // Sans jeton (cookie expiré), rien ne part : chaque saisie recevrait un 401
        if (!getAuthToken()) {
            settle(queuedOp.id, { status: 'queued' });
            return 'network';
        }
        await outbox.markSending(queuedOp.id);
        const op = await outbox.get(queuedOp.id);
        if (!op) return 'done';
        emit({ type: 'op-sending', op });
        try {
            const prepared = await handler.buildRequest(op, context);
            if (prepared.discard) {
                await outbox.remove(op.id);
                await handler.onDiscard?.(op, context, prepared);
                await syncLog?.add({ kind: 'discarded', projectId: op.projectId, opType: op.type, targetKey: op.targetKey, label: describeOp(op), reason: prepared.reason });
                emit({ type: 'op-discarded', op, reason: prepared.reason });
                settle(op.id, { status: 'discarded', reason: prepared.reason });
                return 'done';
            }
            const response = await send(withHeaders({ timeout: DEFAULT_TIMEOUT_MS, ...prepared.request }, op), {
                onUploadProgress: progress => emit({ type: 'op-progress', op, loaded: progress.loaded, total: progress.total })
            });
            // Acceptée par l'API : jamais traitée comme un refus, même si le traitement local échoue
            let result = null;
            try {
                result = await handler.onSuccess(op, response, context, prepared);
            } catch (error) {
                // Traitement local en échec (appareil plein…) : l'opération reste en file et rejoue la même clé, réponse enregistrée par l'API
                console.error(error);
                const localError = { kind: 'retry', reason: 'local_error', message: error?.message || String(error) };
                await outbox.markRetry(op.id, localError, Date.now() + nextRetryDelay(op.attempts));
                emit({ type: 'op-retry', op, error: localError });
                settle(op.id, { status: 'acked', response, result: null });
                return 'done';
            }
            await outbox.remove(op.id);
            emit({ type: 'op-acked', op, response, result, late: !waiters.get(op.id)?.size });
            settle(op.id, { status: 'acked', response, result });
            return 'done';
        } catch (error) {
            // Refus qui signifie « déjà fait » (ex. suppression d'un élément déjà supprimé ailleurs)
            if (handler.isAlreadyDone?.(error, op)) {
                await outbox.remove(op.id);
                await syncLog?.add({ kind: 'discarded', projectId: op.projectId, opType: op.type, targetKey: op.targetKey, label: describeOp(op), reason: 'already_done' });
                emit({ type: 'op-discarded', op, reason: 'already_done' });
                settle(op.id, { status: 'discarded', reason: 'already_done' });
                return 'done';
            }
            const classification = error?.isTransportError
                ? classifyError(error)
                : { kind: 'fatal', reason: 'handler_error', message: error?.message || String(error) };
            // Une 5xx répétée finit en échec : un endpoint qui répond 500 quand sa cible a disparu bouclerait sinon. Compteurs à part :
            // attempts compte aussi les tentatives faites hors-ligne
            const counters = {};
            if (classification.kind === 'retry' && classification.status >= 500) {
                counters.serverErrors = (op.serverErrors || 0) + 1;
                if (counters.serverErrors >= MAX_SERVER_ERROR_ATTEMPTS) Object.assign(classification, { kind: 'fatal', reason: 'server_error' });
            }
            if (classification.kind === 'retry' && classification.reason === 'network' && op.lane === LANES.DATA && (await isApiReachable())) {
                counters.unanswered = (op.unanswered || 0) + 1;
                if (counters.unanswered >= MAX_UNANSWERED_ATTEMPTS) Object.assign(classification, { kind: 'fatal', reason: 'no_response' });
            }
            if (classification.kind === 'retry') {
                await outbox.markRetry(op.id, classification, Date.now() + nextRetryDelay(op.attempts), counters);
                emit({ type: 'op-retry', op, error: classification });
                settle(op.id, { status: 'queued' });
                return 'network';
            }
            await outbox.markFailed(op.id, classification);
            await outbox.blockDependents(op.id);
            emit({ type: 'op-failed', op, error: classification, attended: !!waiters.get(op.id)?.size });
            settle(op.id, { status: 'failed', error });
            return 'done';
        }
    };

    // Vide une voie tant qu'une opération peut partir
    const runLane = async (lane) => {
        while (!stopped) {
            if (Date.now() < laneRetryAt[lane]) return;
            const candidates = await outbox.listSendable(lane, Date.now());
            let processed = false;
            for (const op of candidates) {
                const state = await dependencyState(op);
                if (state === 'waiting') continue;
                if (state === 'blocked') {
                    await outbox.markBlocked(op.id, 'dependency_failed');
                    emit({ type: 'op-blocked', op });
                    continue;
                }
                if (await processOp(op) === 'network') {
                    laneRetryAt[lane] = Date.now() + nextRetryDelay((op.attempts || 0) + 1);
                    return;
                }
                laneRetryAt[lane] = 0;
                processed = true;
                // L'autre voie attend peut-être cette opération ; puis on relit la file, les dépendances ont pu changer
                runLaneLoop(lane === LANES.DATA ? LANES.FILES : LANES.DATA);
                break;
            }
            if (!processed) return;
        }
    };

    const scheduleNext = async () => {
        const queued = stopped ? null : await outbox.nextAttemptTime();
        clearTimeout(timer);
        timer = null;
        if (stopped || queued === null) return;
        const next = Math.max(queued, Math.min(laneRetryAt[LANES.DATA], laneRetryAt[LANES.FILES]));
        // Minimum de 5 s : une opération qui attend une dépendance est relancée par l'autre voie, la minuterie n'est qu'un filet
        const delay = Math.min(Math.max(next - Date.now(), 5000), BACKOFF_MS[BACKOFF_MS.length - 1]);
        timer = setTimeout(() => engine.trigger('timer'), delay);
    };

    // Une passe à la fois par voie ; un déclenchement pendant une passe en demande une autre à la suite
    const runLaneLoop = (lane) => {
        if (runs[lane]) {
            reruns[lane] = true;
            return runs[lane];
        }
        runs[lane] = (async () => {
            try {
                do {
                    reruns[lane] = false;
                    const work = () => runLane(lane);
                    // Un seul onglet synchronise une voie à la fois ; sans Web Locks, on synchronise quand même
                    const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
                    // Verrou obtenu : une opération restée « en cours d'envoi » (onglet fermé pendant l'envoi) repart
                    if (locks?.request) await locks.request(`${LOCK_NAME}-${lane}`, { ifAvailable: true }, async lock => {
                        if (!lock) return;
                        await outbox.resetSending(lane);
                        await work();
                    });
                    else await work();
                } while (reruns[lane] && !stopped);
            } catch (error) {
                console.error(error);
            } finally {
                emit({ type: 'queue-changed', counts: await outbox.counts() });
                await scheduleNext();
            }
        })().finally(() => {
            runs[lane] = null;
            // Relance demandée pendant la fin de la passe : sinon elle serait perdue
            if (reruns[lane] && !stopped) runLaneLoop(lane);
        });
        return runs[lane];
    };

    // Lance les deux voies et attend qu'elles soient au repos
    // Attente commune de la fin des passes, sans en relancer : deux attentes qui relançaient chacune les voies tournaient sans fin
    let idleWait = null;
    const waitIdle = () => idleWait || (idleWait = (async () => {
        // Une voie peut être relancée par l'autre pendant l'attente
        while (!stopped && (runs[LANES.DATA] || runs[LANES.FILES])) await Promise.all([runs[LANES.DATA], runs[LANES.FILES]]);
    })().finally(() => { idleWait = null; }));
    const runAll = () => {
        // Les voies au repos partent tout de suite : une saisie n'attend pas une photo lente
        [LANES.DATA, LANES.FILES].forEach(runLaneLoop);
        return waitIdle();
    };

    const engine = {
        // Démarre le moteur : remet en attente les envois interrompus, puis lance une passe
        start: async () => {
            if (!stopped) return;
            stopped = false;
            await outbox.resetSending();
            await engine.trigger();
        },
        // Arrête le moteur (déconnexion, drapeau coupé)
        stop: () => {
            stopped = true;
            clearTimeout(timer);
            timer = null;
        },
        // Indique si le moteur tourne
        isStarted: () => !stopped,
        // Lance une passe ; force (retour du réseau, « Synchroniser maintenant ») annule les attentes après une coupure
        trigger: async ({ force = false } = {}) => {
            if (stopped) return;
            if (force) {
                laneRetryAt[LANES.DATA] = laneRetryAt[LANES.FILES] = 0;
                await outbox.resetRetryDelays();
            }
            return runAll();
        },
        // Pour les façades : attend l'issue de l'opération au plus waitMs, sinon rend « queued »
        sendNow: (opId, { waitMs = 5000 } = {}) => new Promise(resolve => {
            let done = false, timeout = null;
            const finish = (outcome) => {
                if (done) return;
                done = true;
                clearTimeout(timeout);
                waiters.get(opId)?.delete(finish);
                if (!waiters.get(opId)?.size) waiters.delete(opId);
                resolve(outcome);
            };
            timeout = setTimeout(() => finish({ status: 'queued' }), waitMs);
            if (!waiters.has(opId)) waiters.set(opId, new Set());
            waiters.get(opId).add(finish);
            engine.trigger();
        }),
        // Relance manuelle d'une opération en échec
        retry: async (opId) => {
            await outbox.requeue(opId);
            return engine.trigger();
        },
        // Abandon d'une opération et de celles qui en dépendent, avec retrait dans l'interface (onAbandon)
        abandon: async (opId) => {
            const removed = await outbox.removeWithDependents(opId);
            for (const op of removed) {
                await handlers[op.type]?.onAbandon?.(op, context);
                await syncLog?.add({ kind: 'abandoned', projectId: op.projectId, opType: op.type, targetKey: op.targetKey, label: describeOp(op) });
                emit({ type: 'op-abandoned', op });
                settle(op.id, { status: 'abandoned' });
            }
            emit({ type: 'queue-changed', counts: await outbox.counts() });
            return removed;
        }
    };
    return engine;
};
